import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import i18n from 'i18next';
import {
    Card,
    Row,
    Col,
    Form,
    Button,
    Alert,
    Spinner,
    Container,
} from "react-bootstrap";
import {
    FaUser,
    FaEdit,
    FaSave,
    FaTimes,
    FaEnvelope,
    FaLock,
    FaIdCard,
    FaUserCircle,
    FaSignOutAlt,
} from "react-icons/fa";
import { useAppSelector, useAppDispatch } from "../../store";
import { logout } from "../../features/auth/authSlice";
import { useNavigate } from "react-router-dom";
import { Icon } from "../../components/common/Icon";
import usersService from "../../features/users/usersService";
import { User, UserUpdate } from "../../features/users/usersSlice";
import { useTheme } from "../../context/ThemeContext";
import { useMediaQuery } from "../../hooks/useMediaQuery";

const MyProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAppSelector((state) => state.auth);
    const { theme } = useTheme();
    const isDark = theme === "dark";

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleLogout = () => {
        dispatch(logout());
        navigate("/login");
    };

    const [userData, setUserData] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [formData, setFormData] = useState<Partial<UserUpdate>>({});

    useEffect(() => {
        const fetchUserData = async () => {
            if (!user?.id) return;

            try {
                setIsLoading(true);
                const data = await usersService.getUserById(user.id);
                setUserData(data);
                setFormData({
                    username: data.username,
                    full_name: data.full_name,
                    email: data.email,
                });
            } catch (err) {
                console.error("Error fetching user data:", err);
                setError("Failed to load profile. Please try again.");
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, [user?.id]);

    const handleEdit = () => {
        setIsEditing(true);
        setError(null);
        setSuccess(null);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setFormData({
            username: userData?.username,
            full_name: userData?.full_name,
            email: userData?.email,
            password: undefined,
        });
        setError(null);
        setSuccess(null);
    };

    const handleSave = async () => {
        if (!user?.id) return;

        setIsSaving(true);
        setError(null);

        try {
            const updateData: UserUpdate = {};

            if (formData.username && formData.username !== userData?.username) {
                updateData.username = formData.username;
            }
            if (formData.full_name && formData.full_name !== userData?.full_name) {
                updateData.full_name = formData.full_name;
            }
            if (formData.email && formData.email !== userData?.email) {
                updateData.email = formData.email;
            }
            if (formData.password) {
                updateData.password = formData.password;
            }

            if (Object.keys(updateData).length === 0) {
                setError("No changes to save.");
                setIsSaving(false);
                return;
            }

            const updatedData = await usersService.updateUser(user.id, updateData);
            setUserData(updatedData);
            setFormData({
                username: updatedData.username,
                full_name: updatedData.full_name,
                email: updatedData.email,
                password: undefined,
            });
            setIsEditing(false);
            setSuccess("Profile updated successfully!");

            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            console.error("Error updating profile:", err);
            setError(
                err.response?.data?.detail || "Failed to update profile. Please try again."
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleInputChange = (field: keyof UserUpdate, value: any) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const isMobile = useMediaQuery('(max-width: 991.98px)');

    if (isLoading) {
        return (
            <div className="profile-loading">
                <Spinner animation="border" variant="primary" />
                <p>Loading your profile...</p>
            </div>
        );
    }

    if (!userData) {
        return (
            <div className="profile-error">
                <Icon icon={FaUserCircle} size="3x" />
                <h4>Profile Not Found</h4>
                <p>Unable to load your profile information.</p>
            </div>
        );
    }

    // --- MOBILE VIEW ---
    if (isMobile) {
        return (
            <div className="mobile-profile-page d-flex flex-column" style={{
                background: '#f8fafc', // Very light premium gray
                minHeight: '100%',
                margin: '-20px -16px -20px -16px', // Negative margin to fill sidebar content area
                paddingBottom: '80px' // Space for bottom nav
            }}>
                {/* Premium Simple Header */}
                <div className="d-flex justify-content-center align-items-center px-4 pt-4 pb-2" style={{ minHeight: '80px' }}>
                    <h4 className="m-0 fw-bold text-dark" style={{ fontSize: '1.5rem', letterSpacing: '-0.5px' }}>Profile</h4>
                </div>

                {/* Avatar Section - Floating & Clean */}
                <div className="d-flex flex-column align-items-center justify-content-center py-4">
                    <div className="position-relative mb-3">
                        <div className="d-flex align-items-center justify-content-center rounded-circle" style={{
                            width: '100px',
                            height: '100px',
                            background: 'linear-gradient(135deg, #fff 0%, #f1f5f9 100%)',
                            color: 'var(--brand-primary)',
                            fontSize: '2.5rem',
                            fontWeight: 800,
                            boxShadow: '0 15px 35px -10px rgba(0,0,0,0.1), 0 5px 15px -5px rgba(0,0,0,0.05)',
                            border: '4px solid white'
                        }}>
                            {userData.full_name ? userData.full_name.charAt(0).toUpperCase() : "U"}
                        </div>
                        {/* Status Indicator */}
                        <div style={{
                            position: 'absolute',
                            bottom: '5px',
                            right: '5px',
                            width: '18px',
                            height: '18px',
                            background: '#10b981',
                            borderRadius: '50%',
                            border: '3px solid white'
                        }} />
                    </div>
                    <h2 className="fw-bold m-0 text-dark" style={{ fontSize: '1.4rem', letterSpacing: '-0.5px' }}>{userData.full_name}</h2>
                    <p className="text-muted m-0 small fw-medium">@{userData.username}</p>
                </div>

                {/* Content Card - Fills remaining space */}
                <div className="flex-grow-1 bg-white shadow-sm border-top border-light" style={{
                    borderTopLeftRadius: '32px',
                    borderTopRightRadius: '32px',
                    padding: '32px 24px',
                    overflowY: 'auto' // Allow internal scroll if absolutely necessary on tiny phones
                }}>
                    {/* Alerts - Compact */}
                    {(error || success) && (
                        <div className="mb-4">
                            {error && <Alert variant="danger" className="m-0 py-2 px-3 fs-7 border-0 bg-danger-subtle text-danger rounded-3 fw-medium">{error}</Alert>}
                            {success && <Alert variant="success" className="m-0 py-2 px-3 fs-7 border-0 bg-success-subtle text-success rounded-3 fw-medium">{success}</Alert>}
                        </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h6 className="m-0 fw-bold text-dark" style={{ fontSize: '1.1rem' }}>Personal Info</h6>
                        {!isEditing && (
                            <Button
                                variant="link"
                                className="p-0 text-decoration-none fw-bold"
                                style={{ color: 'var(--brand-primary)', fontSize: '0.9rem' }}
                                onClick={handleEdit}
                            >
                                Edit
                            </Button>
                        )}
                    </div>

                    <div className="d-flex flex-column gap-3">
                        {/* Name Field */}
                        <div className="p-3 bg-light rounded-4 border-0">
                            <label className="d-block text-muted text-uppercase fw-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>Full Name</label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={formData.full_name || ""}
                                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                                    className="w-100 bg-transparent border-0 p-0 fw-semibold text-dark"
                                    style={{ outline: 'none', fontSize: '1rem' }}
                                />
                            ) : (
                                <div className="fw-semibold text-dark" style={{ fontSize: '1rem' }}>{userData.full_name}</div>
                            )}
                        </div>

                        {/* Email Field */}
                        <div className="p-3 bg-light rounded-4 border-0">
                            <label className="d-block text-muted text-uppercase fw-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>Email Address</label>
                            {isEditing ? (
                                <input
                                    type="email"
                                    value={formData.email || ""}
                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                    className="w-100 bg-transparent border-0 p-0 fw-semibold text-dark"
                                    style={{ outline: 'none', fontSize: '1rem' }}
                                />
                            ) : (
                                <div className="fw-semibold text-dark text-truncate" style={{ fontSize: '1rem' }}>{userData.email}</div>
                            )}
                        </div>

                        {/* Password Field (Edit Mode Only) */}
                        {isEditing && (
                            <div className="p-3 bg-light rounded-4 border-0">
                                <label className="d-block text-muted text-uppercase fw-bold mb-1" style={{ fontSize: '0.65rem', letterSpacing: '0.5px' }}>New Password</label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password || ""}
                                    onChange={(e) => handleInputChange("password", e.target.value)}
                                    className="w-100 bg-transparent border-0 p-0 fw-semibold text-dark"
                                    style={{ outline: 'none', fontSize: '1rem' }}
                                />
                            </div>
                        )}
                    </div>

                    {/* Action Buttons (Bottom) */}
                    {isEditing && (
                        <div className="mt-4 d-flex gap-3">
                            <Button variant="light" className="flex-fill rounded-pill py-3 fw-bold text-muted bg-light" onClick={handleCancel}>Cancel</Button>
                            <Button className="flex-fill rounded-pill py-3 fw-bold text-white shadow-lg border-0" onClick={handleSave} disabled={isSaving} style={{ background: 'var(--brand-primary)' }}>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    )}


                </div>
            </div>
        );
    }

    // --- DESKTOP VIEW ---
    return (
        <div className="my-profile-page">
            <Container className="profile-container">
                {/* Alerts */}
                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="profile-alert alert-danger">
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert variant="success" dismissible onClose={() => setSuccess(null)} className="profile-alert alert-success">
                        {success}
                    </Alert>
                )}

                <div className="profile-header-section">
                    <div className="profile-cover"></div>
                    <div className="profile-avatar-container">
                        <div className="avatar-circle">
                            <span className="avatar-initials">
                                {userData.full_name ? userData.full_name.charAt(0).toUpperCase() : "U"}
                            </span>
                        </div>
                    </div>
                    <div className="profile-header-content">
                        <div className="profile-identity">
                            <h2 className="profile-name">{userData.full_name}</h2>
                            <p className="profile-username">@{userData.username}</p>
                        </div>
                        <div className="profile-actions">
                            {!isEditing ? (
                                <Button
                                    variant="primary"
                                    onClick={handleEdit}
                                    className="btn-modern btn-primary"
                                >
                                    <Icon icon={FaEdit} className="me-2" />
                                    Edit Profile
                                </Button>
                            ) : (
                                <div className="edit-actions">
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        className="btn-modern btn-secondary"
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="btn-modern btn-primary"
                                    >
                                        {isSaving ? (
                                            <>
                                                <Spinner animation="border" size="sm" className="me-2" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                Save Changes
                                            </>
                                        )}
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <Row className="mt-4">
                    <Col lg={12} className="mx-auto">
                        <Card className="profile-content-card">
                            <Card.Body className="p-4 p-md-5">
                                <h5 className="section-title">
                                    Personal Information
                                </h5>
                                <Row className="g-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-custom">Username</Form.Label>
                                            <div className="input-wrapper disabled">
                                                <Icon icon={FaIdCard} className="input-icon" />
                                                <Form.Control
                                                    type="text"
                                                    value={formData.username || ""}
                                                    disabled
                                                    className="input-custom"
                                                />
                                            </div>
                                            <Form.Text className="text-muted small mt-1">
                                                Username cannot be changed.
                                            </Form.Text>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label className="form-label-custom">Full Name</Form.Label>
                                            <div className={`input-wrapper ${!isEditing ? 'view-only' : ''}`}>
                                                <Icon icon={FaUser} className="input-icon" />
                                                <Form.Control
                                                    type="text"
                                                    value={formData.full_name || ""}
                                                    onChange={(e) => handleInputChange("full_name", e.target.value)}
                                                    placeholder="Enter full name"
                                                    disabled={!isEditing}
                                                    className="input-custom"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label className="form-label-custom">Email Address</Form.Label>
                                            <div className={`input-wrapper ${!isEditing ? 'view-only' : ''}`}>
                                                <Icon icon={FaEnvelope} className="input-icon" />
                                                <Form.Control
                                                    type="email"
                                                    value={formData.email || ""}
                                                    onChange={(e) => handleInputChange("email", e.target.value)}
                                                    placeholder="Enter email address"
                                                    disabled={!isEditing}
                                                    className="input-custom"
                                                />
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>

                                {isEditing && (
                                    <>
                                        <div className="section-divider"></div>
                                        <h5 className="section-title">
                                            Security
                                        </h5>
                                        <Row>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="form-label-custom">New Password</Form.Label>
                                                    <div className="input-wrapper">
                                                        <Icon icon={FaLock} className="input-icon" />
                                                        <Form.Control
                                                            type="password"
                                                            value={formData.password || ""}
                                                            onChange={(e) => handleInputChange("password", e.target.value)}
                                                            placeholder="Leave blank to keep current password"
                                                            className="input-custom"
                                                        />
                                                    </div>
                                                    <Form.Text className="text-muted small mt-2 d-block">
                                                        Minimum 8 characters. Leave blank if you don't want to change it.
                                                    </Form.Text>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

            </Container>


        </div>
    );
};

export default MyProfilePage;
